import React, { useMemo, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../styles/Signup.css';
import GoogleLoginButton from '../components/GoogleLoginButton';
import api from '../services/api';
import LanguageSelector from '../components/LanguageSelector';

const Signup = () => {
  const { t, i18n } = useTranslation(['auth', 'common', 'errors']);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    preferredLanguage: ''
  });

  const [signupMethod, setSignupMethod] = useState('temp_password');
  const [showMethodDetails, setShowMethodDetails] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [successData, setSuccessData] = useState(null);

  // ========================================
  // 🆕 NOUVEAU : Validation en temps réel
  // ========================================
  const [fieldErrors, setFieldErrors] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: ''
  });

  const [fieldTouched, setFieldTouched] = useState({
    email: false,
    username: false,
    password: false,
    confirmPassword: false
  });

  // Validation email renforcée
  const validateEmail = (email) => {
    if (!email) return { valid: false, message: '' };
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { valid: false, message: t('auth:messages.email_invalid_format') };
    }

    const validDomains = [
      'gmail.com', 'outlook.com', 'hotmail.com', 'yahoo.com', 'yahoo.fr',
      'orange.fr', 'laposte.net', 'free.fr', 'sfr.fr', 'wanadoo.fr',
      'live.com', 'msn.com', 'icloud.com', 'me.com', 'aol.com'
    ];

    const domain = email.split('@')[1]?.toLowerCase();
    if (!validDomains.includes(domain)) {
      return { 
        valid: false, 
        message: t('auth:messages.email_domain_not_supported', { domain })
      };
    }

    return { valid: true, message: '' };
  };

  // 🆕 Validation username
  const validateUsername = (username) => {
    if (!username) return { valid: false, message: '' };
    
    if (username.length < 2) {
      return { valid: false, message: t('auth:messages.username_too_short') };
    }
    
    if (username.length > 30) {
      return { valid: false, message: t('auth:messages.username_too_long') };
    }
    
    if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
      return { 
        valid: false, 
        message: t('auth:messages.username_invalid_chars')
      };
    }

    return { valid: true, message: '' };
  };

  // 🆕 Validation password avec force
  const validatePassword = (password) => {
    if (!password) return { valid: false, message: '', strength: 0 };
    
    let strength = 0;
    const checks = {
      length: password.length >= 8,
      minLength: password.length >= 6,
      hasUpper: /[A-Z]/.test(password),
      hasLower: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };

    if (!checks.minLength) {
      return { 
        valid: false, 
        message: t('auth:messages.password_too_short'),
        strength: 0
      };
    }

    // Calcul force
    if (checks.length) strength += 25;
    if (checks.hasUpper && checks.hasLower) strength += 25;
    if (checks.hasNumber) strength += 25;
    if (checks.hasSpecial) strength += 25;

    return { 
      valid: true, 
      message: '', 
      strength,
      checks
    };
  };

  // 🆕 Validation temps réel à chaque changement
  useEffect(() => {
    if (fieldTouched.email && formData.email) {
      const validation = validateEmail(formData.email);
      setFieldErrors(prev => ({ ...prev, email: validation.message }));
    }
  }, [formData.email, fieldTouched.email, t]);

  useEffect(() => {
    if (fieldTouched.username && formData.username) {
      const validation = validateUsername(formData.username);
      setFieldErrors(prev => ({ ...prev, username: validation.message }));
    }
  }, [formData.username, fieldTouched.username, t]);

  useEffect(() => {
    if (fieldTouched.password && formData.password && signupMethod === 'own_password') {
      const validation = validatePassword(formData.password);
      setFieldErrors(prev => ({ ...prev, password: validation.message }));
    }
  }, [formData.password, fieldTouched.password, signupMethod, t]);

  useEffect(() => {
    if (fieldTouched.confirmPassword && formData.confirmPassword && signupMethod === 'own_password') {
      if (formData.password !== formData.confirmPassword) {
        setFieldErrors(prev => ({ 
          ...prev, 
          confirmPassword: t('auth:messages.passwords_dont_match')
        }));
      } else {
        setFieldErrors(prev => ({ ...prev, confirmPassword: '' }));
      }
    }
  }, [formData.password, formData.confirmPassword, fieldTouched.confirmPassword, signupMethod, t]);

  // Options du menu déroulant
  const signupMethods = useMemo(() => ([
    {
      value: 'temp_password',
      label: t('auth:signup.methods.temp_password.label'),
      description: t('auth:signup.methods.temp_password.description'),
      details: [
        t('auth:signup.methods.temp_password.detail1'),
        t('auth:signup.methods.temp_password.detail2'),
        t('auth:signup.methods.temp_password.detail3')
      ]
    },
    {
      value: 'own_password',
      label: t('auth:signup.methods.own_password.label'),
      description: t('auth:signup.methods.own_password.description'),
      details: [
        t('auth:signup.methods.own_password.detail1'),
        t('auth:signup.methods.own_password.detail2'),
        t('auth:signup.methods.own_password.detail3')
      ]
    },
    {
      value: 'secure_link',
      label: t('auth:signup.methods.secure_link.label'),
      description: t('auth:signup.methods.secure_link.description'),
      details: [
        t('auth:signup.methods.secure_link.detail1'),
        t('auth:signup.methods.secure_link.detail2'),
        t('auth:signup.methods.secure_link.detail3')
      ]
    }
  ]), [t]);

  const selectedMethodInfo = useMemo(
    () => signupMethods.find(m => m.value === signupMethod),
    [signupMethods, signupMethod]
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Nettoyer l'erreur globale quand l'utilisateur tape
    if (error) setError('');
  };

  // 🆕 Marquer le champ comme "touché" quand l'utilisateur quitte le champ
  const handleBlur = (fieldName) => {
    setFieldTouched(prev => ({ ...prev, [fieldName]: true }));
  };

  const handleMethodChange = (e) => {
    const nextMethod = e.target.value;
    setSignupMethod(nextMethod);
    setShowMethodDetails(false);

    if (nextMethod !== 'own_password') {
      setFormData(prev => ({
        ...prev,
        password: '',
        confirmPassword: ''
      }));
      setFieldErrors(prev => ({
        ...prev,
        password: '',
        confirmPassword: ''
      }));
      setFieldTouched(prev => ({
        ...prev,
        password: false,
        confirmPassword: false
      }));
    }
  };

  const handleLanguageChange = (lang) => {
    setFormData(prev => ({ ...prev, preferredLanguage: lang }));
    i18n.changeLanguage(lang);
  };

  const validateForm = () => {
    if (!formData.email || !formData.username) {
      setError(t('auth:messages.email_username_required'));
      return false;
    }

    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.valid) {
      setError(emailValidation.message || t('auth:messages.valid_email_required'));
      return false;
    }

    const usernameValidation = validateUsername(formData.username);
    if (!usernameValidation.valid) {
      setError(usernameValidation.message || t('auth:messages.username_too_short'));
      return false;
    }

    if (signupMethod === 'own_password') {
      if (!formData.password) {
        setError(t('auth:messages.password_required_method'));
        return false;
      }

      const passwordValidation = validatePassword(formData.password);
      if (!passwordValidation.valid) {
        setError(passwordValidation.message || t('auth:messages.password_too_short'));
        return false;
      }

      if (formData.password !== formData.confirmPassword) {
        setError(t('auth:messages.passwords_dont_match'));
        return false;
      }
    }

    return true;
  };

  const passwordsMatch =
    formData.password &&
    formData.confirmPassword &&
    formData.password === formData.confirmPassword;

  // 🆕 Force du mot de passe pour affichage
  const passwordStrength = useMemo(() => {
    if (signupMethod !== 'own_password' || !formData.password) return null;
    return validatePassword(formData.password);
  }, [formData.password, signupMethod]);

  const handleSubmit = async (e) => {

   
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setLoading(true);

    try {
      const payload = {
        email: formData.email,
        username: formData.username,
        signupMethod,
        preferredLanguage: formData.preferredLanguage || i18n.language || 'en'
      };
       const params = new URLSearchParams(window.location.search);
const affSession = params.get('aff_session');
const affCode = params.get('ref'); // optionnel

if (affSession) payload.affiliateSessionId = affSession;
if (affCode) payload.affiliateCodeUsed = affCode;

      if (signupMethod === 'own_password') {
        payload.password = formData.password;
      }

      const { data } = await api.post('/auth/signup', payload);

      if (data?.success) {
        setSuccessData({
          message: data.message,
          accountType: data.account_type,
          token: data.token,
          user: data.user,
          expiresIn: data.expires_in,
        });
        setSuccess(true);

        if (signupMethod === 'own_password' && data.token && data.user) {
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
          api.defaults.headers.common.Authorization = `Bearer ${data.token}`;
            // ✅ AJOUTER CES LIGNES
          if (data.user.preferred_language) {
            localStorage.setItem('language', data.user.preferred_language);
            i18n.changeLanguage(data.user.preferred_language);
          }
          setTimeout(() => navigate('/dashboard'), 1500);
          return;
        }

        setTimeout(() => navigate('/login'), 4000);
        return;
      }

      setError(data?.message || t('auth:messages.signup_error'));
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message;
      const details = err.response?.data?.details; // 🆕 Détails du backend

      // 🆕 Gestion améliorée des erreurs avec détails
      if (status === 409) {
        // Email déjà existant
        setError(msg || t('auth:messages.email_exists'));
        setFieldErrors(prev => ({ 
          ...prev, 
          email: msg || t('auth:messages.email_exists')
        }));
        setFieldTouched(prev => ({ ...prev, email: true }));
      } else if (status === 400) {
        // Données invalides - afficher les détails si disponibles
        if (details && typeof details === 'object') {
          // Le backend renvoie des erreurs détaillées par champ
          const newFieldErrors = {};
          Object.keys(details).forEach(field => {
            newFieldErrors[field] = details[field];
            setFieldTouched(prev => ({ ...prev, [field]: true }));
          });
          setFieldErrors(prev => ({ ...prev, ...newFieldErrors }));
          setError(msg || t('auth:messages.invalid_signup_data'));
        } else {
          setError(msg || t('auth:messages.invalid_signup_data'));
        }
      } else if (status === 422) {
        // Validation error
        setError(msg || t('auth:messages.validation_error'));
      } else if (status === 429) {
        // Too many requests
        setError(t('auth:messages.too_many_attempts'));
      } else {
        setError(msg || t('errors:network'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Page de succès (inchangée)
  if (success && successData) {
    // ... (code existant lines 211-310)
    return <div>Success page content...</div>; // Placeholder
  }

  return (
    <div className="signup-page">
      <div className="signup-container">
        <div className="signup-header">
          <img src="/logo512.png" alt="RizzingBot" style={{ width: 120, height: 120, objectFit: 'contain', marginBottom: 8 }} />
          <h1 className="signup-title">{t('auth:signup.title')}</h1>
          <p className="signup-subtitle">{t('auth:signup.subtitle')}</p>
        </div>

        <div className="signup-google-section">
          <GoogleLoginButton />
          <p className="terms-notice">
            By continuing, you agree to our{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
            {' '}and{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
          </p>
          <div className="signup-divider">
            <span>{t('common:common.or')}</span>
          </div>
        </div>

        {/* 🆕 Message d'erreur global AMÉLIORÉ */}
        {error && (
          <div className="signup-error signup-error-enhanced">
            <div className="signup-error-icon">❌</div>
            <div className="signup-error-content">
              <strong>{t('auth:messages.error_occurred')}</strong>
              <p>{error}</p>
            </div>
          </div>
        )}

        <form className="signup-form" onSubmit={handleSubmit}>
          <LanguageSelector
            value={formData.preferredLanguage}
            onChange={handleLanguageChange}
            autoDetect={true}
          />

          {/* Méthode */}
          <div className="signup-method-section">
            <label className="signup-method-label" htmlFor="signupMethod">
              🎯 {t('auth:signup.method_question')}
            </label>

            <select
              id="signupMethod"
              name="signupMethod"
              className="signup-method-select"
              value={signupMethod}
              onChange={handleMethodChange}
              disabled={loading}
            >
              {signupMethods.map(method => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>

            <button
              type="button"
              className="signup-method-toggle"
              onClick={() => setShowMethodDetails(v => !v)}
              disabled={loading || !selectedMethodInfo}
            >
              {showMethodDetails ? '▲' : '▼'} {t('auth:signup.what_happens')}
            </button>

            {showMethodDetails && selectedMethodInfo && (
              <div className={`signup-method-description ${signupMethod}`}>
                <p className="signup-method-description-text">
                  {selectedMethodInfo.description}
                </p>
                <ul className="signup-method-details">
                  {selectedMethodInfo.details.map((detail, idx) => (
                    <li key={idx}>{detail}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* 🆕 Email avec validation visuelle */}
          <div className="signup-form-group">
            <label className="signup-form-label" htmlFor="email">
              📧 {t('auth:signup.email_label')}
            </label>
            <div className="signup-input-wrapper">
              <input
                id="email"
                name="email"
                type="email"
                className={`signup-form-input ${
                  fieldTouched.email && fieldErrors.email ? 'error' : ''
                } ${
                  fieldTouched.email && !fieldErrors.email && formData.email ? 'success' : ''
                }`}
                placeholder={t('auth:signup.email_placeholder')}
                value={formData.email}
                onChange={handleInputChange}
                onBlur={() => handleBlur('email')}
                required
                disabled={loading}
                autoComplete="email"
              />
              {fieldTouched.email && !fieldErrors.email && formData.email && (
                <span className="signup-input-icon success">✓</span>
              )}
              {fieldTouched.email && fieldErrors.email && (
                <span className="signup-input-icon error">✗</span>
              )}
            </div>
            
            {/* 🆕 Message d'erreur sous le champ */}
            {fieldTouched.email && fieldErrors.email && (
              <div className="signup-field-error">
                {fieldErrors.email}
              </div>
            )}
            
            {!fieldErrors.email && (
              <small className="signup-field-hint">
                {t('auth:signup.email_help')}
              </small>
            )}
          </div>

          {/* 🆕 Username avec validation visuelle */}
          <div className="signup-form-group">
            <label className="signup-form-label" htmlFor="username">
              👤 {t('auth:signup.username_label')}
            </label>
            <div className="signup-input-wrapper">
              <input
                id="username"
                name="username"
                type="text"
                className={`signup-form-input ${
                  fieldTouched.username && fieldErrors.username ? 'error' : ''
                } ${
                  fieldTouched.username && !fieldErrors.username && formData.username ? 'success' : ''
                }`}
                placeholder={t('auth:signup.username_placeholder')}
                value={formData.username}
                onChange={handleInputChange}
                onBlur={() => handleBlur('username')}
                required
                disabled={loading}
                autoComplete="username"
              />
              {fieldTouched.username && !fieldErrors.username && formData.username && (
                <span className="signup-input-icon success">✓</span>
              )}
              {fieldTouched.username && fieldErrors.username && (
                <span className="signup-input-icon error">✗</span>
              )}
            </div>
            
            {fieldTouched.username && fieldErrors.username && (
              <div className="signup-field-error">
                {fieldErrors.username}
              </div>
            )}
          </div>

          {/* 🆕 Password fields avec indicateur de force */}
          {signupMethod === 'own_password' && (
            <div className="signup-password-fields">
              <div className="signup-form-group">
                <label className="signup-form-label" htmlFor="password">
                  🔒 {t('auth:signup.password_label')}
                </label>
                <div className="signup-password-input-container">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    className={`signup-form-input ${
                      fieldTouched.password && fieldErrors.password ? 'error' : ''
                    }`}
                    placeholder={t('auth:signup.password_placeholder')}
                    value={formData.password}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur('password')}
                    required
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="signup-password-toggle"
                    disabled={loading}
                    aria-label={showPassword ? t('auth:signup.hide_password') : t('auth:signup.show_password')}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>

                {/* 🆕 Erreur mot de passe */}
                {fieldTouched.password && fieldErrors.password && (
                  <div className="signup-field-error">
                    {fieldErrors.password}
                  </div>
                )}

                {/* 🆕 Indicateur de force du mot de passe */}
                {formData.password && !fieldErrors.password && passwordStrength && (
                  <div className="signup-password-strength">
                    <div className="signup-password-strength-bar">
                      <div 
                        className={`signup-password-strength-fill strength-${
                          passwordStrength.strength >= 75 ? 'strong' :
                          passwordStrength.strength >= 50 ? 'medium' : 'weak'
                        }`}
                        style={{ width: `${passwordStrength.strength}%` }}
                      />
                    </div>
                    <div className="signup-password-strength-label">
                      {passwordStrength.strength >= 75 && '💪 ' + t('auth:signup.password_strong')}
                      {passwordStrength.strength >= 50 && passwordStrength.strength < 75 && '👍 ' + t('auth:signup.password_medium')}
                      {passwordStrength.strength < 50 && '⚠️ ' + t('auth:signup.password_weak')}
                    </div>
                    
                    {/* 🆕 Conseils pour améliorer */}
                    {passwordStrength.strength < 100 && (
                      <div className="signup-password-tips">
                        <small>{t('auth:signup.password_tips')}:</small>
                        <ul>
                          {!passwordStrength.checks?.length && (
                            <li>✗ {t('auth:signup.password_tip_length')}</li>
                          )}
                          {!(passwordStrength.checks?.hasUpper && passwordStrength.checks?.hasLower) && (
                            <li>✗ {t('auth:signup.password_tip_case')}</li>
                          )}
                          {!passwordStrength.checks?.hasNumber && (
                            <li>✗ {t('auth:signup.password_tip_number')}</li>
                          )}
                          {!passwordStrength.checks?.hasSpecial && (
                            <li>✗ {t('auth:signup.password_tip_special')}</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Confirm password */}
              <div className="signup-form-group">
                <label className="signup-form-label" htmlFor="confirmPassword">
                  🔒 {t('auth:signup.confirm_password_label')}
                </label>
                <div className="signup-password-input-container">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    className={`signup-form-input ${
                      fieldTouched.confirmPassword && fieldErrors.confirmPassword ? 'error' : ''
                    } ${
                      fieldTouched.confirmPassword && !fieldErrors.confirmPassword && formData.confirmPassword ? 'success' : ''
                    }`}
                    placeholder={t('auth:signup.confirm_password_placeholder')}
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur('confirmPassword')}
                    required
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(v => !v)}
                    className="signup-password-toggle"
                    disabled={loading}
                    aria-label={showConfirmPassword ? t('auth:signup.hide_password') : t('auth:signup.show_password')}
                  >
                    {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>

                {formData.confirmPassword && (
                  <div className={`signup-password-validation ${passwordsMatch ? 'success' : 'error'}`}>
                    {passwordsMatch ? (
                      <>
                        <span>✓</span>
                        {t('auth:signup.passwords_match')}
                      </>
                    ) : (
                      <>
                        <span>✗</span>
                        {t('auth:messages.passwords_dont_match')}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Terms & Conditions checkbox */}
          <div className="signup-terms">
            <label className="signup-checkbox-label">
              <input
                type="checkbox"
                checked={agreeToTerms}
                onChange={(e) => setAgreeToTerms(e.target.checked)}
                disabled={loading}
                className="signup-checkbox"
              />
              <span className="signup-checkbox-text">
                I agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              </span>
            </label>
          </div>

          {/* Button */}
          <button
            type="submit"
            className="signup-button"
            disabled={loading || !agreeToTerms || (signupMethod === 'own_password' && !passwordsMatch)}
          >
            {loading ? (
              <>
                <span className="signup-button-icon">⏳</span>
                {t('auth:signup.creating')}
              </>
            ) : (
              <>
                <span className="signup-button-icon">🚀</span>
                {t('auth:signup.signup_button')}
              </>
            )}
          </button>
        </form>

        <div className="signup-link">
          <p>{t('auth:signup.already_have_account')}</p>
          <Link to="/login">{t('auth:signup.login_link')}</Link>
        </div>

        <div className="signup-benefits">
          <div className="signup-benefit">✨ {t('auth:signup.benefits.free_credits')}</div>
          <div className="signup-benefit">🤖 {t('auth:signup.benefits.optimized_ai')}</div>
          <div className="signup-benefit">📊 {t('auth:signup.benefits.psychological_analysis')}</div>
        </div>
      </div>
    </div>
  );
};

export default Signup;